import { storeToRefs } from 'pinia';
import { useCartStore } from '@/features/Cart/stores/cart';
import type {
  CartUrlActionItem,
  CartUrlActionOptions,
  CartUrlNotice,
  CartUrlNoticeItem,
} from '@/features/Cart/types/models';
import type { AddToCartItem, ShopifyCart } from '@/features/Cart/types/servicesShopifyCart';
import { useShopifyCartService } from '@/features/Cart/services/shopifyCart';
import { enqueueCartMutation } from '@/features/Cart/services/cartMutationQueue';
import { getLineIdentity } from '@/features/Cart/services/lineItemProperties';
import type { CartMutation } from '@/features/Cart/services/cartMutationResults';
import {
  clearCartUrlActionRecord,
  createCartUrlActionRecord,
  getCartUrlActionItems,
  getCartUrlActionUndo,
  hasCartUrlActionAdditions,
  hasUnexpectedCartChanges,
  isCartUrlActionComplete,
  readCartUrlActionRecord,
  writeCartUrlActionRecord,
  type CartUrlActionRecord,
} from '@/features/Cart/services/cartUrlAction';
import { useProductStockStore } from '@/features/Product/stores/productStock';

type CartUrlActionFlowOptions = {
  syncCart: () => Promise<ShopifyCart | null>;
  mutateAndSync: CartMutation;
  hasPendingStockSwap: () => boolean;
};

let activeCartUrlActionId: string | null = null;

export const cartUrlActionFlow = ({
  syncCart,
  mutateAndSync,
  hasPendingStockSwap,
}: CartUrlActionFlowOptions) => {
  const { updateCart, getCart, addToCart } = useShopifyCartService();
  const cartStore = useCartStore();
  const { setCartUrlNotice, isFreeGiftItem } = cartStore;
  const { cartData, cartUrlNotice } = storeToRefs(cartStore);
  const { refreshStock } = useProductStockStore();

  const getUnresolvedCartUrlActionRecord = (): CartUrlActionRecord | null => {
    const record = readCartUrlActionRecord();
    return record && record.id !== activeCartUrlActionId ? record : null;
  };

  const markCartUrlActionPending = (notice: CartUrlNotice) => {
    setCartUrlNotice(notice);
  };

  const finishCartUrlAction = (notice: CartUrlNotice | null) => {
    clearCartUrlActionRecord();
    setCartUrlNotice(notice);
  };

  const dismissCartUrlNotice = () => {
    if (activeCartUrlActionId) return;
    if (cartUrlNotice.value || readCartUrlActionRecord()) finishCartUrlAction(null);
  };

  const releaseCartUrlAction = () => {
    if (activeCartUrlActionId || !readCartUrlActionRecord()) return;
    finishCartUrlAction(cartUrlNotice.value);
  };

  const resolveCartUrlActionRecord = (record: CartUrlActionRecord, cart: ShopifyCart) => {
    if (record.phase === 'prepared') {
      finishCartUrlAction({ type: 'not-added', items: [] });
      return;
    }
    if (cart.token !== record.token) {
      finishCartUrlAction({ type: 'cart-changed', items: [] });
      return;
    }
    const items = getCartUrlActionItems(record, cart);
    if (hasUnexpectedCartChanges(record, cart, isFreeGiftItem)) {
      markCartUrlActionPending({ type: 'cart-changed', items });
      return;
    }
    if (isCartUrlActionComplete(items)) {
      finishCartUrlAction({ type: 'added', items });
      return;
    }
    markCartUrlActionPending({
      type: hasCartUrlActionAdditions(items) ? 'partly-added' : 'unconfirmed',
      items,
    });
  };

  const markCartUrlActionUnconfirmed = (items: CartUrlNoticeItem[]): boolean => {
    markCartUrlActionPending({
      type: hasCartUrlActionAdditions(items) ? 'partly-added' : 'unconfirmed',
      items,
    });
    return false;
  };

  const undoCartUrlAction = async (record: CartUrlActionRecord): Promise<boolean> => {
    const cart = await getCart().catch((error: unknown) => {
      console.error('Failed to read the cart before removing URL cart items', error);
      return null;
    });
    if (!cart) return markCartUrlActionUnconfirmed([]);
    const updates = getCartUrlActionUndo(record, cart);
    if (Object.keys(updates).length) {
      await updateCart(updates).catch((error: unknown) =>
        console.error('Failed to remove URL cart items', error),
      );
    }
    if (!(await syncCart())) return markCartUrlActionUnconfirmed([]);
    const items = getCartUrlActionItems(record, cartData.value);
    if (hasCartUrlActionAdditions(items)) return markCartUrlActionUnconfirmed(items);
    if (hasUnexpectedCartChanges(record, cartData.value, isFreeGiftItem)) {
      clearCartUrlActionRecord();
      markCartUrlActionPending({ type: 'cart-changed', items });
      return false;
    }
    finishCartUrlAction({ type: 'not-added', items: [] });
    return false;
  };

  const runCartUrlAction = async (
    record: CartUrlActionRecord,
    items: AddToCartItem[],
    isRequestedCartSettled: (cart: ShopifyCart) => boolean,
  ): Promise<boolean> => {
    const requestedIdentities = new Set(record.requested.map(({ identity }) => identity));
    const { error, fetchedCart } = await mutateAndSync(
      async () => {
        if (!writeCartUrlActionRecord({ ...record, phase: 'sent' })) {
          throw new Error('Could not save the cart URL action record');
        }
        await addToCart(items);
      },
      (item) => requestedIdentities.has(getLineIdentity(item.variant_id, item.properties)),
    );
    if (error) console.error('Failed to add URL cart items', error);
    if (!fetchedCart) return markCartUrlActionUnconfirmed([]);
    const isStockRefreshed = await refreshStock({ force: true });
    const cart = cartData.value;
    const isAdded =
      isStockRefreshed &&
      isCartUrlActionComplete(getCartUrlActionItems(record, cart)) &&
      !hasUnexpectedCartChanges(record, cart, isFreeGiftItem) &&
      isRequestedCartSettled(cart);
    if (!isAdded) return undoCartUrlAction(record);
    finishCartUrlAction(null);
    return true;
  };

  const addCartUrlItems = (
    urlItems: CartUrlActionItem[],
    { findUnavailableTitles, isRequestedCartSettled }: CartUrlActionOptions,
  ): Promise<boolean> =>
    enqueueCartMutation(async () => {
      const isCartSynced = !!(await syncCart()) && !hasPendingStockSwap();
      if (readCartUrlActionRecord()) return false;
      if (!isCartSynced) {
        setCartUrlNotice({ type: 'not-added', items: [] });
        return false;
      }
      const cartBefore = cartData.value;
      const unavailableTitles = findUnavailableTitles(cartBefore);
      if (unavailableTitles.length) {
        setCartUrlNotice({
          type: 'unavailable',
          items: unavailableTitles.map((title) => ({ title })),
        });
        return false;
      }
      const record = createCartUrlActionRecord(cartBefore, urlItems, isFreeGiftItem);
      if (!writeCartUrlActionRecord(record)) {
        setCartUrlNotice({ type: 'not-added', items: [] });
        return false;
      }
      activeCartUrlActionId = record.id;
      try {
        return await runCartUrlAction(
          record,
          urlItems.map(({ item }) => item),
          isRequestedCartSettled,
        );
      } finally {
        activeCartUrlActionId = null;
      }
    });

  return {
    getUnresolvedCartUrlActionRecord,
    markCartUrlActionPending,
    resolveCartUrlActionRecord,
    dismissCartUrlNotice,
    releaseCartUrlAction,
    addCartUrlItems,
  };
};
